import i18n from '../locales/i18n';
import jaJP from 'antd/locale/ja_JP';
import enUS from 'antd/locale/en_US';
import '../styles/index.css';
import React, { useCallback, useEffect, useState } from 'react';
import { ConfigProvider, Flex, Image, message } from 'antd';
import { signOut, getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import { Loading, APIService, registerAmplify, SignIn, themeList } from '@meteor/frontend-core';
import { AppContext } from '../contexts/AppContext';
import useStorage from '../hooks/useStorage';
import { defaultTheme } from '../consts/common/default-configuration';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
import mockList from '../http/APIMock';
import IndexedDBLocalStorage from '../utils/indexDbStorage';
import APIList from '../http/ApiList';
import { DIMENSION, EVALUATION_PERIOD, PERIOD } from '../enum/common.enum';
import { groupBy, maxBy, minBy } from 'lodash';
import isoWeek from 'dayjs/plugin/isoWeek';
import { defaultFontSize, defaultSize } from '../hooks/useTralitSetting';
import Dashboard from './dashboard';
import ErrorPage from './error';
import ToshibaDashboard from './toshiba-dashboard';
import { useEventEmitter } from 'ahooks';

dayjs.locale('ja');
dayjs.extend(isoWeek);

APIService.env = process.env;
export const PREFIXCLS = 'tralit';

const App: React.FC = () => {
  const indexDbStorage = new IndexedDBLocalStorage();
  const { getItem, setItem } = useStorage();

  const event$ = useEventEmitter();

  const chartThemeName = process.env.REACT_APP_CHART_THEME ? process.env.REACT_APP_CHART_THEME : 'toshiba';
  const themeName = process.env.REACT_APP_THEME && defaultTheme[process.env.REACT_APP_THEME] ? process.env.REACT_APP_THEME : 'toshiba';

  themeList[chartThemeName] = {
    color: [
      '#005AC6', // 中蓝
      '#5B3872', // 中紫
      '#7A7A7A', // 中灰
      '#004199', // 深蓝
      '#422D5C', // 深紫
      '#5E5E5E', // 深灰
      '#4C9AE6', // 浅蓝
      '#8E7FAA', // 浅紫
      '#A1A1A1', // 浅灰
    ],
  };

  const navigate = useNavigate();
  const location = useLocation();

  APIService.mockApiList = mockList;
  APIService.env = process.env;
  registerAmplify(process.env);

  const [authInfo, setAuthInfo] = useState<any>({
    disableAuth: process.env.REACT_APP_DISABLE_AUTH === 'true',
    loading: process.env.REACT_APP_DISABLE_AUTH !== 'true',
    user: null,
    loggedOut: true,
    signOut: async () => {
      await signOut();
      setAuthInfo({ ...authInfo, user: null });
      await navigate('/auth/login', { replace: true });
    },
  });
  const [globalEvent, setGlobalEvent] = useState<any>(null);

  const settings = localStorage.getItem('tralitSettings')
    ? JSON.parse(localStorage.getItem('tralitSettings'))
    : {
        account: 'toshiba',
        key: 'eg9g8i7o3brq',
        defaultFiscalYear: 'FY25',
        fontSize: '大',
        ...Object.keys(defaultFontSize).reduce((acc, key) => {
          acc[key] = defaultFontSize[key][defaultSize];
          return acc;
        }, {}),
      };
  const [tralitSettings, setTralitSettings] = useState({ ...settings });

  const [chatEvent, setChatEvent] = useState<any>(null);
  const [menuCollapsed, setMenuCollapsed] = useState<boolean>(false);
  const [selectSessionId, setSelectSessionId] = useState<string>(null);
  const [color, setColor] = useState<any>();
  const [configuration, setConfiguration] = useState<any>(defaultTheme[themeName].configuration);
  const [echartTheme, setEchartTheme] = useState<any>(chartThemeName);
  const [customEchartTheme, setCustomEchartTheme] = useState<any>({});
  const [customAntChartTheme, setCustomAntChartTheme] = useState<any>({});
  const [contextLoading, setContextLoading] = useState<boolean>(false);
  const selectedLang = getItem('language', 'local') || 'ja';
  const [locale, setLocale] = useState(selectedLang === 'ja' ? jaJP : enUS);
  const [allComponents, setAllComponents] = useState<any>(null);
  const [linksCollapsed, setLinksCollapsed] = useState<any>(true);
  const [scrollCount, setScrollCount] = useState(0);

  const [selectedModule, setSelectedModule] = useState<any>({
    title: (
      <Flex>
        <Image
          preview={false}
          style={{ position: 'relative', marginLeft: 10, width: 135, height: 21 }}
          src={process.env.REACT_APP_DOMAIN + 'assets/Toshiba_logo.png'}
        />
        <div style={{ fontSize: 24, paddingLeft: 12, height: 50 }}>
          <span style={{ fontWeight: 'normal', fontSize: 24 }}></span>AI Powered Management Cockpit
        </div>
      </Flex>
    ),
    footerText: configuration.footerText,
  });

  const setDefaultConfiguration = () => {
    const list: any = {};
    list[themeName] = {
      configuration: defaultTheme[themeName].configuration,
      colors: defaultTheme[themeName].color,
    };
    setItem('currentTheme', themeName, 'local');
    setItem('themeList', JSON.stringify(list), 'local');
  };

  useEffect(() => {
    i18n.changeLanguage(selectedLang);
    let settingsColor: any = null;
    const fontFamily = getItem('fontFamily', 'local');
    if (fontFamily) {
      document.body.style.fontFamily = fontFamily;
    } else {
      setItem(
        'fontFamily',
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
        'local'
      );
    }
    const customEchartThemeLocal = getItem('customEchartTheme', 'local');
    if (customEchartThemeLocal && process.env.REACT_APP_FORCE_SETTINGS !== 'true') {
      setCustomEchartTheme(JSON.parse(customEchartThemeLocal));
    }
    const customAntChartThemeLocal = getItem('customAntChartTheme', 'local');
    if (customAntChartThemeLocal && process.env.REACT_APP_FORCE_SETTINGS !== 'true') {
      setCustomAntChartTheme(JSON.parse(customAntChartThemeLocal));
    }
    const currentEchartTheme = getItem('echartTheme', 'local');
    if (currentEchartTheme && process.env.REACT_APP_FORCE_SETTINGS !== 'true') {
      setEchartTheme(currentEchartTheme);
    }
    const currentTheme = getItem('currentTheme', 'local');
    if (currentTheme) {
      const themeString = getItem('themeList', 'local');
      if (themeString) {
        const themeInfo = JSON.parse(themeString);
        if (themeInfo[currentTheme]) {
          const themeConfiguration = themeInfo[currentTheme].configuration;
          if (themeConfiguration) {
            setConfiguration(themeConfiguration);
            const themeColors = themeInfo[currentTheme].colors;
            if (themeColors) {
              const themeColor = themeColors[themeConfiguration.theme];
              if (themeColor) {
                settingsColor = themeColor;
              }
            }
          }
        }
      }
    }
    if (!settingsColor || process.env.REACT_APP_FORCE_SETTINGS === 'true') {
      setDefaultConfiguration();
      const { theme } = defaultTheme[themeName].configuration;
      settingsColor = defaultTheme[themeName].color[theme];
    }
    setColor(settingsColor);
  }, []);

  useEffect(() => {
    if (!authInfo.disableAuth && !authInfo.user && location.pathname !== '/auth/login') {
      getCurrentUser()
        .then(async (user) => {
          const attributes = await fetchUserAttributes();
          const client_account = attributes['custom:client_account'];
          if (!client_account) {
            navigate('/error');
          }
          setAuthInfo({
            ...authInfo,
            loading: false,
            loggedOut: false,
            user: { ...user, attributes },
          });
        })
        .catch((error: any) => {
          setAuthInfo({
            ...authInfo,
            loading: false,
            loggedOut: error && error === 'The user is not authenticated',
          });
          navigate('/auth/login');
        });
    } else if (location.pathname === '/auth/login') {
      setAuthInfo({
        ...authInfo,
        loading: false,
      });
    }
  }, [location]);

  // calender
  const [calender, setCalender] = useState([]);
  const getCalenderMap = useCallback(
    (snapshot) => {
      const nowDay = dayjs(snapshot);
      const findMondays = (calenderData, evaluationPeriod) => {
        let filteredCalender = calenderData;

        switch (evaluationPeriod) {
          // 全期
          case EVALUATION_PERIOD.FULL_PERIOD:
            break;
          // 上期
          case EVALUATION_PERIOD.PREVIOUS_PERIOD:
            filteredCalender = calenderData.filter((item) => PERIOD.PREVIOUS === item.halfPeriod);
            break;
          // 下期
          case EVALUATION_PERIOD.NEXT_PERIOD:
            filteredCalender = calenderData.filter((item) => PERIOD.NEXT === item.halfPeriod);
            break;
          // 実績のみ
          case EVALUATION_PERIOD.ACTUAL_ONLY:
            filteredCalender = calenderData.filter((item) => {
              // const monday = nowDay.subtract(1, 'week').isoWeekday(1);
              // 修改基准日为snapshot的前一天
              const yesterday = nowDay.subtract(1, 'month');
              return dayjs(item.monday).isBefore(yesterday);
            });
            break;
          // 見込のみ
          case EVALUATION_PERIOD.FORECAST_ONLY:
            filteredCalender = calenderData.filter((item) => {
              const monday = nowDay.isoWeekday(1);
              return dayjs(item.monday).isAfter(monday) || dayjs(item.monday).isSame(monday);
            });
            break;
          // 今月まで
          case EVALUATION_PERIOD.UP_TO_THIS_MONTH:
            filteredCalender = calenderData.filter((item) => {
              // 修改基准日为snapshot的前一天
              const yesterday = nowDay.subtract(1, 'day');
              const nowYearMonth = dayjs(`${yesterday.year()}-${yesterday.month() + 1}`);
              const compareYearMonth = dayjs(`${item.year}-${item.month}`);
              return nowYearMonth.isAfter(compareYearMonth) || nowYearMonth.isSame(compareYearMonth);
            });
            break;
          // 今月単月
          case EVALUATION_PERIOD.THIS_MONTH_ONLY:
            filteredCalender = calenderData.filter((item) => {
              // 修改基准日为snapshot的前一天
              const yesterday = nowDay.subtract(1, 'day');
              const curMonday = dayjs(`${yesterday.year()}-${yesterday.month()}`);
              const compareYearMonth = dayjs(`${dayjs(item.date).year()}-${dayjs(item.date).month()}`);
              return curMonday.isSame(compareYearMonth);
            });
            break;
          // 先週単週
          case EVALUATION_PERIOD.LAST_WEEK_ONLY:
            filteredCalender = calenderData.filter((item) => {
              const monday = nowDay.subtract(1, 'week').isoWeekday(1);
              return monday.isSame(dayjs(item.monday));
            });
            break;
          // LAST_12WEEK_ONLY
          case EVALUATION_PERIOD.LAST_12WEEK_ACTUAL:
            filteredCalender = calenderData.filter((item) => {
              const endMonday = nowDay.isoWeekday(1);
              const startMonday = endMonday.subtract(12, 'week').isoWeekday(1);
              return (
                dayjs(item.monday).isBefore(endMonday) &&
                (dayjs(item.monday).isAfter(startMonday) || dayjs(item.monday).isSame(startMonday))
              );
            });
            break;
        }
        const min = minBy<Calender>(filteredCalender, (item) => dayjs(item.date).valueOf());
        const max = maxBy<Calender>(filteredCalender, (item) => dayjs(item.date).valueOf());
        const minDate = min ? dayjs(min.date) : null;
        const maxDate = max ? dayjs(max.date) : null;
        return {
          [DIMENSION.WEEK]: Object.keys(groupBy(filteredCalender, 'monday')).map((monday) => dayjs(monday)),
          [DIMENSION.DAY]: Object.keys(groupBy(filteredCalender, 'date')).map((date) => dayjs(date)),
          [DIMENSION.MONTH]: Object.keys(groupBy(filteredCalender, (item) => `${item.year}-${item.month}`)).map((yearMonth) =>
            dayjs(yearMonth)
          ),
          period: [minDate, maxDate],
        };
      };

      const gbFY = groupBy(calender as any[], 'fiscalYear');
      const map: any = {
        ...Object.keys(gbFY).reduce(
          (acc, fy) => {
            acc[fy] = {
              [EVALUATION_PERIOD.THIS_MONTH_ONLY]: findMondays(gbFY[fy], EVALUATION_PERIOD.THIS_MONTH_ONLY),
              [EVALUATION_PERIOD.LAST_WEEK_ONLY]: findMondays(gbFY[fy], EVALUATION_PERIOD.LAST_WEEK_ONLY),
              [EVALUATION_PERIOD.FULL_PERIOD]: findMondays(gbFY[fy], EVALUATION_PERIOD.FULL_PERIOD),
              [EVALUATION_PERIOD.PREVIOUS_PERIOD]: findMondays(gbFY[fy], EVALUATION_PERIOD.PREVIOUS_PERIOD),
              [EVALUATION_PERIOD.NEXT_PERIOD]: findMondays(gbFY[fy], EVALUATION_PERIOD.NEXT_PERIOD),
              [EVALUATION_PERIOD.UP_TO_THIS_MONTH]: findMondays(gbFY[fy], EVALUATION_PERIOD.UP_TO_THIS_MONTH),
              [EVALUATION_PERIOD.ACTUAL_ONLY]: findMondays(gbFY[fy], EVALUATION_PERIOD.ACTUAL_ONLY),
              [EVALUATION_PERIOD.FORECAST_ONLY]: findMondays(gbFY[fy], EVALUATION_PERIOD.FORECAST_ONLY),
            };
            return acc;
          },
          {
            [EVALUATION_PERIOD.LAST_12WEEK_ACTUAL]: findMondays(calender, EVALUATION_PERIOD.LAST_12WEEK_ACTUAL),
          }
        ),
      };
      return map;
    },
    [calender]
  );

  useEffect(() => {
    APIList.getCalender()
      .get()
      .then((calender) => {
        setCalender(calender);
      });
  }, []);

  return (
    <div className="dashboard">
      {color ? (
        <AppContext.Provider
          value={{
            globalEvent,
            setGlobalEvent,
            authInfo,
            setAuthInfo,
            contextLoading,
            setContextLoading,
            color,
            setColor,
            locale,
            setLocale,
            configuration,
            setConfiguration,
            echartTheme,
            setEchartTheme,
            customEchartTheme,
            setCustomEchartTheme,
            customAntChartTheme,
            setCustomAntChartTheme,
            selectedModule,
            menuCollapsed,
            setMenuCollapsed,
            chatEvent,
            setChatEvent,
            selectSessionId,
            setSelectSessionId,
            indexDbStorage,
            allComponents,
            setAllComponents,
            linksCollapsed,
            setLinksCollapsed,
            getCalenderMap,
            scrollCount,
            setScrollCount,
            event$,
            tralitSettings,
            setTralitSettings,
            calender,
          }}
        >
          <ConfigProvider
            locale={locale}
            prefixCls={PREFIXCLS}
            theme={{
              token: {
                colorPrimary: color.primaryColor,
                colorSuccess: color.successColor,
                colorWarning: color.warningColor,
                colorError: color.errorColor,
                colorInfo: color.infoColor,
                colorLink: color.primaryColor,
                borderRadius: 4,
              },
            }}
          >
            {authInfo.loading ? (
              <Loading />
            ) : (
              <>
                <Routes>
                  <Route
                    path="/auth/login"
                    element={
                      <SignIn
                        federatedIdentifyProvider={process.env.REACT_APP_FEDERATED_IDENTIFY_PROVIDER}
                        backgroundImageUrl={`${process.env.REACT_APP_DOMAIN}assets/backgroundpic.jpg`}
                      />
                    }
                  />
                  <Route path="/tralit/forecast" element={<Dashboard />} />
                  <Route path="/tralit/summary" element={<Dashboard />} />
                  <Route path="/tralit/compare" element={<Dashboard />} />
                  <Route path="/tralit/risk" element={<Dashboard />} />
                  <Route path="/toshiba/achieve-summary" element={<ToshibaDashboard />} />
                  <Route path="/toshiba/achieve-detail" element={<ToshibaDashboard />} />
                  <Route path="/toshiba/impact-analysis" element={<ToshibaDashboard />} />
                  <Route path="/toshiba/achieve-diff-analysis" element={<ToshibaDashboard />} />
                  <Route path="/error" element={<ErrorPage />} />
                  <Route path="*" element={<Navigate to="/tralit/forecast" replace />} />
                </Routes>
              </>
            )}
          </ConfigProvider>
        </AppContext.Provider>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default App;

export type CalenderMap = {
  [EVALUATION_PERIOD.LAST_12WEEK_ACTUAL]: {
    [DIMENSION.WEEK]: Dayjs[];
    [DIMENSION.DAY]: Dayjs[];
    [DIMENSION.MONTH]: Dayjs[];
    period: [Dayjs, Dayjs];
  };
} & {
  [fy in string]: {
    [evaluationPeriod in EVALUATION_PERIOD]: {
      [DIMENSION.WEEK]: Dayjs[];
      [DIMENSION.DAY]: Dayjs[];
      [DIMENSION.MONTH]: Dayjs[];
      period: [Dayjs, Dayjs];
    };
  };
};

export type Calender = {
  date: string;
  year: string;
  fiscalYear: string;
  month: number;
  weekDay: string;
  weekNum: number;
  monday: string;
  halfPeriod: string;
  quarter: string;
  previousYear: string;
  previousMonthSameWeek: string;
};
